/**
 * ImageThumbnailChip Component
 * 
 * Displays a compact thumbnail representation of an image search message
 * with image preview, caption (if provided), and generated keywords.
 * Clicking the thumbnail opens a larger preview in a lightbox modal.
 */

import React, { useState } from 'react';
import { Box, Typography, Dialog, IconButton, Backdrop } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

interface ImageThumbnailChipProps {
  imageData?: string;        // Base64 encoded image data
  caption?: string;          // Optional user-provided caption
  keywords?: string;         // Generated keywords (not displayed, kept for compatibility)
  isProcessing?: boolean;    // Whether image is still being analyzed
  textColor?: string;        // Text color from theme configuration
}

const ImageThumbnailChip: React.FC<ImageThumbnailChipProps> = ({
  imageData,
  caption,
  // keywords not used - only for backward compatibility
  isProcessing = false,
  textColor = 'text.primary',
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const imageSrc = imageData 
    ? (imageData.startsWith('data:') ? imageData : `data:image/jpeg;base64,${imageData}`)
    : null;

  const handleThumbnailClick = () => {
    if (imageSrc && !isProcessing) {
      setLightboxOpen(true);
    }
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          padding: '8px 12px',
          borderRadius: '16px',
          backgroundColor: 'rgba(66, 133, 244, 0.1)',
          border: '1px solid rgba(66, 133, 244, 0.3)',
          maxWidth: '100%',
          flexWrap: 'wrap',
        }}
      >
        {/* Image thumbnail or icon */}
        {imageSrc ? (
          <Box
            onClick={handleThumbnailClick}
            sx={{
              position: 'relative',
              cursor: isProcessing ? 'default' : 'pointer',
              '&:hover .zoom-indicator': {
                opacity: isProcessing ? 0 : 1,
              },
            }}
          >
            <Box
              component="img"
              src={imageSrc}
              alt={caption || 'Uploaded image'}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                objectFit: 'cover',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: isProcessing ? 'none' : 'scale(1.05)',
                },
              }}
              onError={(e) => {
                // Fallback to icon if image fails to load
                const target = e.target as HTMLElement;
                target.style.display = 'none';
              }}
            />
            {/* Zoom indicator on hover */}
            {!isProcessing && (
              <Box
                className="zoom-indicator"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                }}
              >
                <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              backgroundColor: 'rgba(66, 133, 244, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageIcon sx={{ color: 'rgb(66, 133, 244)' }} />
          </Box>
        )}

        {/* Text content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Caption or processing status */}
          {isProcessing ? (
            <Typography
              variant="body2"
              sx={{
                color: textColor,
                fontStyle: 'italic',
                fontSize: '0.875rem',
                opacity: 0.8,
              }}
            >
              Analyzing image...
            </Typography>
          ) : caption ? (
            <Typography
              variant="body2"
              sx={{
                color: textColor,
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              {caption}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: textColor,
                fontSize: '0.875rem',
                opacity: 0.8,
              }}
            >
              Smart Matched
            </Typography>
          )}
        </Box>
      </Box>

      {/* Lightbox Modal for larger image preview */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: '90vw',
            maxHeight: '90vh',
            margin: 2,
          },
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: 'absolute',
              top: -48,
              right: 0,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
            aria-label="Close image preview"
          >
            <CloseIcon />
          </IconButton>

          {/* Large image */}
          {imageSrc && (
            <Box
              component="img"
              src={imageSrc}
              alt={caption || 'Uploaded image'}
              sx={{
                maxWidth: '90vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 2,
              }}
            />
          )}

          {/* Caption below image */}
          {caption && (
            <Typography
              sx={{
                color: 'white',
                mt: 2,
                textAlign: 'center',
                fontSize: '1rem',
              }}
            >
              {caption}
            </Typography>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ImageThumbnailChip;
